
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";

const NavLinks = () => {
  const { user } = useAuth();
  const { isPremiumUser, isAdmin, adminLoading } = usePremiumAccess();
  const [hasStartupAccount, setHasStartupAccount] = useState(false);

  useEffect(() => {
    const checkStartupAccount = async () => {
      if (!user) {
        setHasStartupAccount(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("startup_accounts")
          .select("id, status")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!error && data) {
          setHasStartupAccount(true);
        } else {
          setHasStartupAccount(false);
        }
      } catch (error) {
        console.error("Error checking startup account:", error);
        setHasStartupAccount(false);
      }
    };

    checkStartupAccount();
  }, [user]);

  // Show premium dashboard link only for premium users OR admin users (but wait for admin loading)
  const showPremiumDashboard = !adminLoading && (isPremiumUser || isAdmin);
  const showAdminDashboard = !adminLoading && isAdmin;

  return (
    <nav className="flex items-center space-x-6">
      {/* Only show these links to non-authenticated users */}
      {!user && (
        <>
          <Link 
            to="/" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Home
          </Link>
          
          <Link 
            to="/teen-of-the-day" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Teen of the Day
          </Link>
          
          <Link 
            to="/startups" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Startups
          </Link>
          
          <Link 
            to="/founders" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Founders
          </Link>
          
          <Link 
            to="/pricing" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Pricing
          </Link>
        </>
      )}
      
      {/* Show different links based on whether user has a startup account - NO HOME LINK */}
      {user && !hasStartupAccount && (
        <>
          <Link 
            to="/teen-of-the-day" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Teen of the Day
          </Link>
          <Link 
            to="/dashboard" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Dashboard
          </Link>
          <Link 
            to="/opportunities" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Opportunities
          </Link>
          <Link 
            to="/clubs" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Clubs
          </Link>
          <Link 
            to="/events" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Events
          </Link>
          <Link 
            to="/mentors" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Mentors
          </Link>
          <Link 
            to="/pitch-playground" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Pitch
          </Link>
          {showPremiumDashboard && (
            <Link 
              to="/premium-dashboard" 
              className="text-sm font-medium text-[#FF8C00] hover:text-orange-600 transition-colors duration-200"
            >
              {isAdmin ? '✨ Premium Features' : '✨ Premium Dashboard'}
            </Link>
          )}
          {showAdminDashboard && (
            <Link 
              to="/admin" 
              className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors duration-200 font-semibold"
            >
              🛡️ Admin Panel
            </Link>
          )}
        </>
      )}

      {/* Show startup-specific navigation for startup accounts - NO HOME LINK */}
      {user && hasStartupAccount && (
        <>
          <Link 
            to="/teen-of-the-day" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Teen of the Day
          </Link>
          <Link 
            to="/teenvisor" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Startup Dashboard
          </Link>
          <Link 
            to="/pricing" 
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            Premium
          </Link>
          {showPremiumDashboard && (
            <Link 
              to="/premium-dashboard" 
              className="text-sm font-medium text-[#FF8C00] hover:text-orange-600 transition-colors duration-200"
            >
              {isAdmin ? '✨ Premium Features' : '✨ Premium Dashboard'}
            </Link>
          )}
          {showAdminDashboard && (
            <Link 
              to="/admin" 
              className="text-sm font-medium text-red-600 hover:text-red-700 transition-colors duration-200 font-semibold"
            >
              🛡️ Admin Panel
            </Link>
          )}
        </>
      )}
    </nav>
  );
};

export default NavLinks;
